import React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import Clickable from 'components/Common/Clickable'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import { merchandiseMegaMenuBreakpoints } from './merchandiseMegaMenuBreakpoints'
import { useMegaMenuTracking } from 'analytics/snowplow/events/megaMenuEvents'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(24)};
  padding: ${rem(24)};
`

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(16)};
  width: 100%;
`

const LettersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: ${rem(8)};
  width: 100%;

  ${merchandiseMegaMenuBreakpoints.ultraWide} {
    grid-template-columns: repeat(7, 1fr);
  }

  ${merchandiseMegaMenuBreakpoints.mediumLarge} {
    grid-template-columns: repeat(5, 1fr);
  }
`

const LetterButton = styled(Clickable)`
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 1;
  padding: ${rem(10)} ${rem(12)};
  border: 1px solid ${props => props.theme.palette.neutral.default.four};
  border-radius: ${rem(2)};
  background: ${props => props.theme.palette.neutral.default.eight};
  transition: background-color 0.2s, border-color 0.2s;

  ${merchandiseMegaMenuBreakpoints.compact} {
    padding: ${rem(6)} ${rem(8)};
    font-size: ${rem(12)};
  }

  ${mediaHoverable} {
    &:hover {
      background: ${props => props.theme.palette.neutral.default.seven};
    }
  }
`

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ#'.split('')

interface Props {
  onClose: () => void
}

function MerchandiseBrandsAlphabet(props: Props) {
  const { onClose } = props
  const intl = useIntl()
  const trackLetterClick = useMegaMenuTracking('brands_az', onClose)
  const trackViewAllClick = useMegaMenuTracking('view_all_brands', onClose)

  return <Container>
    <Header>
      <BodyText variant="medium" colour="neutral-two">
        {intl.formatMessage({
          id: 'KinpnnAk9U',
          defaultMessage: 'Brands A-Z',
          description: 'Header text for alphabetical brand list',
        })}
      </BodyText>
    </Header>
    <LettersGrid>
      {ALPHABET.map((letter, index) => (
        <LetterButton
          key={letter}
          onClick={() => trackLetterClick({ id: letter, name: letter }, index)}
          to={`/shop/brands#${letter}`}
        >
          <BodyText variant="medium" colour="neutral-one">
            {letter}
          </BodyText>
        </LetterButton>
      ))}
    </LettersGrid>
    <TextLink
      to="/shop/brands"
      onClick={() => trackViewAllClick({ id: 'view_all_brands', name: 'View All Brands' })}
      colour="neutral-two"
      size="medium"
      weight="regular"
    >
      {intl.formatMessage({
        id: '+ed5MZHFOM',
        defaultMessage: 'View All Brands',
        description: 'Link text to view all merchandise brands',
      })}
    </TextLink>
  </Container>
}

export default MerchandiseBrandsAlphabet
